import { useEffect, type CSSProperties } from 'react';
import type { PublicSiteInfo } from '@barbeiropro/contracts';

type Theme = PublicSiteInfo['theme'];

function lum(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
}
const contrast = (a: string, b: string) => {
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p);
  return (x! + 0.05) / (y! + 0.05);
};
/** Cor de texto (clara ou escura) com melhor contraste sobre `bg`. */
export function readable(bg: string) {
  const light = '#fbf9f4';
  const dark = '#15120e';
  return contrast(bg, light) >= contrast(bg, dark) ? light : dark;
}

const FONTS = {
  classic: { display: "'Big Shoulders Display','Arial Narrow',sans-serif", transform: 'uppercase', weight: '800', spacing: '0.01em', lh: '0.92' },
  modern: { display: "'Instrument Sans',system-ui,sans-serif", transform: 'none', weight: '700', spacing: '-0.025em', lh: '1' },
  editorial: { display: "'Fraunces',Georgia,serif", transform: 'none', weight: '600', spacing: '-0.02em', lh: '1.02' },
} as const;

export interface Tone {
  bg: string;
  ink: string;
}

export function toneVars(t: Tone): CSSProperties {
  return {
    '--s-bg': t.bg,
    '--s-ink': t.ink,
    '--s-muted': `color-mix(in srgb, ${t.ink} 64%, ${t.bg})`,
    '--s-line': `color-mix(in srgb, ${t.ink} 20%, ${t.bg})`,
    backgroundColor: t.bg,
    color: t.ink,
  } as CSSProperties;
}

export function themeVars(t: Theme): CSSProperties {
  const f = FONTS[t.fontPair] ?? FONTS.classic;
  return {
    ...toneVars({ bg: t.background, ink: readable(t.background) }),
    '--s-primary': t.primary,
    '--s-on-primary': readable(t.primary),
    '--s-accent': t.accent,
    '--s-on-accent': readable(t.accent),
    '--s-display': f.display,
    '--s-display-transform': f.transform,
    '--s-display-weight': f.weight,
    '--s-display-spacing': f.spacing,
    '--s-display-lh': f.lh,
    fontFamily: "'Instrument Sans',system-ui,sans-serif",
  } as CSSProperties;
}

export const displayStyle: CSSProperties = {
  fontFamily: 'var(--s-display)',
  textTransform: 'var(--s-display-transform)' as CSSProperties['textTransform'],
  fontWeight: 'var(--s-display-weight)' as unknown as number,
  letterSpacing: 'var(--s-display-spacing)',
  lineHeight: 'var(--s-display-lh)',
};

/** Fraunces só é carregada quando o tema editorial está ativo. */
export function useThemeFonts(fontPair: Theme['fontPair']) {
  useEffect(() => {
    if (fontPair !== 'editorial' || document.getElementById('site-font-fraunces')) return;
    const link = Object.assign(document.createElement('link'), {
      id: 'site-font-fraunces',
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&display=swap',
    });
    document.head.appendChild(link);
  }, [fontPair]);
}

export const WEEKDAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

/** Agrupa dias com o mesmo horário: "Seg a Sex  09:00 às 19:00". */
export function groupHours(hours: { weekday: number; opensAt: string; closesAt: string }[]) {
  const short = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const byDay = new Map<number, string[]>();
  for (const h of hours) byDay.set(h.weekday, [...(byDay.get(h.weekday) ?? []), `${h.opensAt} às ${h.closesAt}`]);
  const order = [1, 2, 3, 4, 5, 6, 0];
  const groups: { days: number[]; label: string }[] = [];
  for (const d of order) {
    const label = byDay.get(d)?.join(' · ');
    if (!label) continue;
    const last = groups[groups.length - 1];
    if (last && last.label === label && order.indexOf(last.days[last.days.length - 1]!) === order.indexOf(d) - 1) last.days.push(d);
    else groups.push({ days: [d], label });
  }
  return groups.map((g) => ({
    days: g.days.length > 1 ? `${short[g.days[0]!]} a ${short[g.days[g.days.length - 1]!]}` : short[g.days[0]!]!,
    label: g.label,
  }));
}

export const digitsOnly = (v: string) => v.replace(/\D/g, '');
export const waLink = (n: string | null) => (n ? `https://wa.me/55${digitsOnly(n)}` : null);
export const instaLink = (v: string | null) => (v ? (/^https?:\/\//.test(v) ? v : `https://instagram.com/${v.replace(/^@/, '')}`) : null);
